import React from 'react';
import styled from 'styled-components';

import useDefaultCurrency from 'hooks/useDefaultCurrency';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { formatMetricValue, orbitResultFormat } from '../chartAdapters';
import { cohortChips, cohortValueLabel, formatCohortDate } from '../cohortLabels';
import { OrbitCohort } from '../cohortTypes';
import { TOrbit } from '../i18n/TModules';
import { OrbitChip, OrbitChipRow, orbitTokens, orbitType } from '../styles';

export interface IOrbitCohortSummaryProps {
  metricId: string;
  cohort: OrbitCohort;
}

const Root = styled.div`
  > * + * {
    margin-top: 8px;
  }
`;

const Line = styled.div`
  color: ${orbitTokens.textMuted};
  ${orbitType.helping}
`;

const SelectedLine = styled.div`
  color: ${orbitTokens.textSecondary};
  ${orbitType.containerBody}
`;

const OrbitCohortSummary: React.FC<IOrbitCohortSummaryProps> = ({ metricId, cohort }) => {
  const { t } = useMultiLanguageTranslation();
  const currency = useDefaultCurrency() ?? undefined;
  const chips = cohortChips(t, cohort.applied);
  const date = formatCohortDate(cohort.data_as_of);
  const caption = [
    cohort.n_population === null
      ? null
      : t(`${TOrbit}.cohort.population`, { customers: formatMetricValue(cohort.n_population) }),
    date === null ? null : t(`${TOrbit}.cohort.dataAsOf`, { date })
  ].filter((part): part is string => part !== null);
  const { selected } = cohort;

  const renderSelected = (): React.ReactNode => {
    if (selected === null || selected.dimension_keys.length === 0) {
      return null;
    }

    const bands = selected.dimension_keys
      .map(key => cohortValueLabel(t, selected.dimension, key))
      .join(', ');
    const readable = !selected.suppressed && selected.missing.length === 0;
    const parts = [
      t(`${TOrbit}.cohort.selected`, { bands }),
      readable && selected.value !== null
        ? formatMetricValue(selected.value, orbitResultFormat(metricId, cohort), currency)
        : t(`${TOrbit}.metrics.noValue`),
      readable && selected.n_customers !== null
        ? t(`${TOrbit}.cohort.selectedCustomers`, {
            count: selected.n_customers,
            total: formatMetricValue(selected.n_customers)
          })
        : null
    ].filter((part): part is string => part !== null);

    return (
      <SelectedLine data-testid={`orbit-cohort-selected-${metricId}`}>
        {parts.join(' · ')}
      </SelectedLine>
    );
  };

  return (
    <Root data-testid={`orbit-cohort-${metricId}`}>
      {chips.length > 0 ? (
        <OrbitChipRow role="list" aria-label={t(`${TOrbit}.cohort.filtersLabel`)}>
          {chips.map(chip => (
            <OrbitChip
              key={chip.tag}
              role="listitem"
              data-testid={`orbit-cohort-filter-${metricId}-${chip.tag}`}
            >
              {chip.label}
            </OrbitChip>
          ))}
        </OrbitChipRow>
      ) : null}
      {renderSelected()}
      {caption.length > 0 ? (
        <Line data-testid={`orbit-cohort-caption-${metricId}`}>{caption.join(' · ')}</Line>
      ) : null}
    </Root>
  );
};

export default OrbitCohortSummary;
